import {
	Input,
	InputNumber,
	Radio,
	TreeSelect,
	Modal,
	Button,
	Card,
	message,
	Tooltip,
} from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import './index.scss';
import { AllocationSettingsProps } from './types';
import { BlueAddSvg, DeleteActionSvg, EditActionSvg } from 'utils/svgs';
import WrappingEllipse from 'components/CostAllocation/Ellipse';
// Helper to ensure section 6 exists in memory
export const ensureIndirectSection = (settingsData: any) => {
	const copy = { ...settingsData };
	if (!copy['6']) {
		copy['6'] = {
			id: '6',
			type: 'allocationRates',
			fields: {},
			addMore: true,
			toolTip:
				'Define multiple allocations rates, each with its own Account, Customer, Class, Location and Rate.',
			placeHolder: 'Select values',
			errorMessage: 'Please complete all fields for allocation rates.',
			qbMappingValue: '',
			capMappingTitle: 'Allocation Rates',
		};
	}
	return copy;
};

const migrateSingleAllocationToSection6 = (settingsData: any) => {
	const copy = ensureIndirectSection(settingsData);

	// If section 6 has no fields, always create a default allocation
	if (Object.keys(copy['6'].fields).length === 0) {
		const account = copy['4']?.fields?.['f1']?.value;
		const customer = copy['5']?.fields?.['f1']?.value;
		const classValue = copy['0']?.fields?.['f2']?.value;

		// Always create default allocation with "Indirect Rate" label
		// Use values from other sections if available, otherwise null
		copy['6'].fields['f1'] = {
			id: 'f1',
			label: 'Indirect Rate',
			account: account || null,
			customer: customer || null,
			class: classValue || null,
			location: null,
			rate: 0, // Default rate
			editable: true,
			deletable: true,
			isEditing: false,
			isActive: true,
			priority: 1,
		};
	}

	return copy;
};

// Allocation settings component
const AllocationSettings: FC<AllocationSettingsProps> = (props) => {
	const {
		settingsData,
		// changePayrollMethodHandler,
		payrollMethod,
		// indirectExpenseRate,
		// changeExpenseRateAllocationHandler,
		settingsChangeHandler,
		qbSelectionObj,
		addFieldHandler,
		deleteFieldHandler,
	} = props;

	const [filteredSettingsData, setFilteredSettingsData] = useState({});
	const [validationError, setValidationError] = useState<string>('');
	const localSettingsData = useMemo(() => {
		return migrateSingleAllocationToSection6(settingsData);
	}, [settingsData]);

	// State to handle delete modal visibility
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [fieldToDelete, setFieldToDelete] = useState<{
		sectionId: string;
		fieldId: string;
		label: string;
	} | null>(null);

	// const getMappingValue = (
	// 	sectionId: string,
	// 	fieldId: string,
	// 	fieldName: string
	// ) => {
	// 	try {
	// 		return settingsData?.[sectionId]?.fields?.[fieldId]?.[fieldName];
	// 	} catch {
	// 		return undefined;
	// 	}
	// };

	const getOptions = (type: string) => {
		if (!qbSelectionObj) return [];
		if (type === 'qbCoa') return qbSelectionObj.qbCoa || [];
		if (type === 'qbClass') return qbSelectionObj.qbClass || [];
		if (type === 'qbCustomer') return qbSelectionObj.qbCustomer || [];
		if (type === 'qbLocation') return qbSelectionObj.qbLocation || [];
		return [];
	};

	const getTreeData = (type: string) => {
		const options = getOptions(type);
		console.log('options', type, options);
		return options
			.filter((item: any) => item.Id)
			.map((item: any) => ({
				title:
					item.title ||
					item.Name ||
					item.DisplayName ||
					item.FullyQualifiedName,
				value: item.Id,
				key: item.Id,
				disabled: item.disabled === true,
				children: item.children
					? item.children.map((child: any) => ({
							title:
								child.title || child.DisplayName || child.FullyQualifiedName,
							value: child.Id,
							key: child.Id,
							disabled: child.disabled === true,
					  }))
					: [],
			}));
	};

	// const handleSelectChange = (
	// 	sectionId: string,
	// 	fieldId: string,
	// 	fieldName: string,
	// 	value: any
	// ) => {
	// 	settingsChangeHandler({
	// 		sectionId,
	// 		fieldId,
	// 		fieldName,
	// 		dataId: value,
	// 	});
	// };

	// Calculate total allocation percentage
	const calculateTotalAllocation = () => {
		if (!localSettingsData['6']?.fields) return 0;
		return Object.values(localSettingsData['6'].fields).reduce(
			(total: number, field: any) => {
				return total + (field.rate || 0);
			},
			0
		);
	};

	// Validate allocations
	// const validateAllocations = () => {
	// 	const totalAllocation = calculateTotalAllocation();
	// 	const allocationCount = Object.keys(localSettingsData['6']?.fields || {}).length;

	// 	// Check if total exceeds 100%
	// 	if (totalAllocation > 100) {
	// 		setValidationError('Total allocation cannot exceed 100%');
	// 		return false;
	// 	}

	// 	// Check if count exceeds 5
	// 	if (allocationCount >= 5) {
	// 		return false;
	// 	}

	// 	// Clear error if validation passes
	// 	setValidationError('');
	// 	return true;
	// };

	// Enhanced settings change handler with validation
	const handleSettingsChange = (changeData: any) => {
		settingsChangeHandler(changeData);

		// Validate immediately for rate changes
		if (changeData.fieldName === 'rate') {
			// Calculate total including the new value
			const currentTotal = calculateTotalAllocation();
			const newValue = changeData.dataId || 0;
			const oldValue =
				localSettingsData['6']?.fields?.[changeData.fieldId]?.rate || 0;
			const adjustedTotal = currentTotal - oldValue + newValue;

			if (adjustedTotal > 100) {
				setValidationError('Total allocation cannot exceed 100%');
			} else {
				setValidationError('');
			}
		}
	};

	// Enhanced add field handler with validation
	const handleAddField = () => {
		const allocationCount = Object.keys(
			localSettingsData['6']?.fields || {}
		).length;

		if (allocationCount >= 5) {
			return;
		}

		addFieldHandler('6');
	};

	// Function to show delete confirmation modal
	const showDeleteModal = (
		sectionId: string,
		fieldId: string,
		label: string
	) => {
		setFieldToDelete({ sectionId, fieldId, label });
		setIsDeleteModalVisible(true);
	};

	// Function to handle the delete action
	const handleDelete = () => {
		if (fieldToDelete) {
			const allocationCount = Object.keys(
				localSettingsData['6']?.fields || {}
			).length;

			// Only delete if there are multiple allocations
			if (allocationCount > 1) {
				// Get the rate of the field being deleted to recalculate total
				const deletedFieldRate =
					localSettingsData['6']?.fields?.[fieldToDelete.fieldId]?.rate || 0;

				deleteFieldHandler(fieldToDelete.sectionId, fieldToDelete.fieldId);
				message.success('Allocation deleted successfully!');

				// Immediately recalculate and validate after deletion
				const newTotalAllocation =
					calculateTotalAllocation() - deletedFieldRate;
				if (newTotalAllocation > 100) {
					setValidationError('Total allocation cannot exceed 100%');
				} else {
					setValidationError('');
				}
			}
		}
		setIsDeleteModalVisible(false);
		setFieldToDelete(null);
	};

	// Function to close the modal without doing anything
	const handleCancelDelete = () => {
		setIsDeleteModalVisible(false);
		setFieldToDelete(null);
	};

	// Enhanced delete field handler (now shows modal instead of direct deletion)
	const handleDeleteField = (sectionId: string, fieldId: string) => {
		const allocationCount = Object.keys(
			localSettingsData['6']?.fields || {}
		).length;

		// Only show modal if there are multiple allocations
		if (allocationCount > 1) {
			const field = localSettingsData['6']?.fields?.[fieldId];
			showDeleteModal(sectionId, fieldId, field?.label || 'Untitled');
		}
	};

	useEffect(() => {
		const newData = JSON.parse(JSON.stringify(settingsData));
		delete newData['0'];
		delete newData['4'];
		delete newData['5'];
		setFilteredSettingsData(newData);
	}, [settingsData]);

	useEffect(() => {
		// Validate on data changes (but not for rate changes which are handled in handleSettingsChange)
		const totalAllocation = calculateTotalAllocation();
		if (totalAllocation > 100) {
			setValidationError('Total allocation cannot exceed 100%');
		} else {
			setValidationError('');
		}
	}, [localSettingsData]);

	const totalAllocation = calculateTotalAllocation();
	const allocationCount = Object.keys(
		localSettingsData['6']?.fields || {}
	).length;
	const canAddMore = allocationCount < 5;

	// JSX
	return (
		<div className={styles['account-settings']}>
			<div className={styles['account-settings__wrapper']}>
				{localSettingsData['6'] && (
					<div className={styles['account-settings__indirect-allocations']}>
						<div className={styles['account-settings__top']}>
							<Tooltip
								title={localSettingsData['6'].toolTip}
								placement="top"
							>
								<p className={styles['account-settings__top--title']}>
									Allocation Rates
								</p>
							</Tooltip>
						</div>

						<div className={styles['account-settings__main']}>
							{Object.values(localSettingsData['6'].fields)
								.sort((a: any, b: any) => a.priority - b.priority)
								.map((field: any, index: number) => (
									<div
										key={field.id}
										className={
											styles['account-settings__center--indirect-allocations']
										}
									>
										{/* Left Side - Name */}
										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--item'
												]
											}
										>
											{field.isEditing ? (
												<Input
													size="large"
													style={{
														width: '90%',
														fontSize: '12px',
														height: '38px',
													}}
													value={field.label}
													onChange={(event) =>
														handleSettingsChange({
															sectionId: '6',
															fieldId: field.id,
															fieldName: 'label',
															dataId: event.target.value,
														})
													}
													onBlur={(event) => {
														const value = event.target.value.trim();
														if (value === '') {
															return;
														}
														handleSettingsChange({
															sectionId: '6',
															fieldId: field.id,
															fieldName: 'isEditing',
															dataId: false,
														});
													}}
													autoFocus
													className={
														styles[
															'account-settings__center--indirect-allocations--inputName'
														]
													}
												/>
											) : (
												<div
													className={
														styles[
															'account-settings__center--indirect-allocations--label'
														]
													}
													style={{
														display: 'flex',
														alignItems: 'center',
														flexWrap: 'nowrap',
													}}

													// onClick={() =>
													// 	handleSettingsChange({
													// 		sectionId: '6',
													// 		fieldId: field.id,
													// 		fieldName: 'isEditing',
													// 		dataId: true,
													// 	})
													// }
												>
													<WrappingEllipse
														message={`${field.label || 'Untitled'}`}
														maxLength={15} // Adjust based on your container width
														maxLines={1}
														isTooltip={true}
														tooltipMessage={field.label || 'Untitled'}
													/>
													{allocationCount > 1 && (
														<span className="required-field">*</span>
													)}
												</div>
											)}
										</div>

										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--actions'
												]
											}
										>
											{/* Right Side - All other elements */}
											<div
												style={{
													marginRight: '10px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													padding: '8px',
													borderRadius: '50%',
													boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
													transition: 'background-color 0.3s',
													fontSize: '12px',
												}}
												className="cursor-pointer"
												onClick={() =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'isEditing',
														dataId: true,
													})
												}
											>
												<EditActionSvg />
											</div>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													padding: '8px',
													borderRadius: '50%',
													boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
													transition: 'background-color 0.3s',
													marginRight: '25px',
													opacity: allocationCount === 1 ? 0.5 : 1,
													cursor:
														allocationCount === 1 ? 'not-allowed' : 'pointer',
												}}
												className={
													allocationCount === 1 ? '' : 'cursor-pointer'
												}
												onClick={() =>
													allocationCount > 1 &&
													handleDeleteField('6', field.id)
												}
											>
												<DeleteActionSvg />
											</div>
										</div>
										{/* Account */}
										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--item'
												]
											}
										>
											{/* <Select
												placeholder="Account"
												className={styles['account-settings__center--indirect-allocations--select']}
												value={field.account}
												onChange={(val) =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'account',
														dataId: val,
													})
												}
												style={{ width: 180 }}
												size="large"
												allowClear
											>
												{getOptions('qbCoa').map((opt: any) => (
													<Select.Option key={opt.Id} value={opt.Id}>
														{opt.Name}
													</Select.Option>
												))}
											</Select> */}
											{index === 0 && (
												<label className={styles['field-label']}>Account</label>
											)}
											<TreeSelect
												placeholder="Account"
												className={
													styles[
														'account-settings__center--indirect-allocations--select'
													]
												}
												value={field.account}
												onChange={(val) =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'account',
														dataId: val,
													})
												}
												treeData={getTreeData('qbCoa')}
												size="large"
												treeDefaultExpandAll
												allowClear
												showSearch
												filterTreeNode={(input: any, treeNode: any) =>
													treeNode.title
														.toLowerCase()
														.includes(input.toLowerCase())
												}
											/>
										</div>

										{/* Customer */}
										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--item'
												]
											}
										>
											{index === 0 && (
												<label className={styles['field-label']}>
													Customer
												</label>
											)}
											<TreeSelect
												placeholder="Customer"
												className={
													styles[
														'account-settings__center--indirect-allocations--select'
													]
												}
												value={field.customer}
												onChange={(val) =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'customer',
														dataId: val,
													})
												}
												treeData={getTreeData('qbCustomer')}
												size="large"
												treeDefaultExpandAll
												allowClear
												showSearch
												filterTreeNode={(input: any, treeNode: any) =>
													treeNode.title
														.toLowerCase()
														.includes(input.toLowerCase())
												}
											/>
										</div>

										{/* Class */}
										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--item'
												]
											}
										>
											{index === 0 && (
												<label className={styles['field-label']}>Class</label>
											)}
											<TreeSelect
												placeholder="Class"
												className={
													styles[
														'account-settings__center--indirect-allocations--select'
													]
												}
												value={field.class}
												onChange={(val) =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'class',
														dataId: val,
													})
												}
												treeData={getTreeData('qbClass')}
												size="large"
												treeDefaultExpandAll
												allowClear
												showSearch
												filterTreeNode={(input: any, treeNode: any) =>
													treeNode.title
														.toLowerCase()
														.includes(input.toLowerCase())
												}
											/>
										</div>
										{/* <div className={styles['account-settings__center--indirect-allocations--item']}>
											{index === 0 && <label className={styles['field-label']}>Location</label>}
											<TreeSelect
												placeholder="Location"
												className={styles['account-settings__center--indirect-allocations--select']}
												value={field.location}
												onChange={(val) =>
													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'location',
														dataId: val,
													})
												}
												treeData={getTreeData('qbLocation')}
												size="large"
												treeDefaultExpandAll
												allowClear
												showSearch
												filterTreeNode={(input: any, treeNode: any) =>
													treeNode.title.toLowerCase().includes(input.toLowerCase())
												}
											/>
										</div> */}

										{/* Rate Input */}
										<div
											className={
												styles[
													'account-settings__center--indirect-allocations--item'
												]
											}
										>
											<InputNumber
												placeholder="Rate"
												className={`${
													styles[
														'account-settings__center--indirect-allocations--input'
													]
												} ${
													styles[
														'account-settings__center--indirect-allocations--input-rate'
													]
												} ${totalAllocation > 100 ? styles['error'] : ''}`}
												value={field.rate || 0}
												onChange={(val) => {
													// Trim and handle empty values
													let processedValue = val;
													if (val === null || val === undefined || val === '') {
														processedValue = 0;
													}

													handleSettingsChange({
														sectionId: '6',
														fieldId: field.id,
														fieldName: 'rate',
														dataId: processedValue,
													});
												}}
												onBlur={(e) => {
													// Ensure value is 0 if empty on blur
													const value = e.target.value;
													if (
														value === '' ||
														value === null ||
														value === undefined
													) {
														handleSettingsChange({
															sectionId: '6',
															fieldId: field.id,
															fieldName: 'rate',
															dataId: 0,
														});
													}
												}}
												style={{ width: 120 }}
												size="large"
												min={0}
												max={100}
												addonAfter={<span style={{ fontSize: '12px' }}>%</span>}
											/>
										</div>
									</div>
								))}

							{/* Error Message and Add Another Item - Same line with space-between */}
							<div className={styles['mapping-box__bottom']}>
								{/* Error Message - Left side (only when there's an error) */}
								{validationError ? (
									<div className="error-message">{validationError}</div>
								) : (
									<div></div> /* Empty div to maintain flex layout */
								)}

								{/* Add Another Item - Right side (always when under 5 allocations) */}
								{canAddMore && (
									<div
										onClick={handleAddField}
										className={styles['mapping-box__bottom--add-button']}
									>
										<BlueAddSvg />
										<p className={styles['mapping-box__bottom--add-more']}>
											Add Another Item
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{payrollMethod === 'Hours' && (
					<div className={styles['account-settings__bottom']}>
						<div className={styles['account-settings__bottom--title']}>
							<p>Rates limited for Salaried-Non-Exempt and Hourly employees</p>
						</div>
						{Object.values(filteredSettingsData).map(
							(singleSettingsObj: any) => {
								return Object.values(singleSettingsObj.fields).map(
									(field: any, index: number) => {
										return (
											<div
												className={styles['account-settings__bottom--select']}
												key={index}
											>
												<p>{field.label}</p>
												<Radio.Group
													defaultValue={field.ratesLimited}
													onChange={(event) =>
														handleSettingsChange({
															sectionId: singleSettingsObj.id,
															fieldId: field.id,
															fieldName: 'ratesLimited',
															dataId: event.target.value,
														})
													}
												>
													<Radio value={true}>Yes</Radio>
													<Radio value={false}>No</Radio>
												</Radio.Group>
											</div>
										);
									}
								);
							}
						)}
					</div>
				)}
			</div>

			{/* Multiple Indirect Allocations Section */}

			{/* Delete Confirmation Modal */}
			{isDeleteModalVisible && (
				<Modal
					title={
						<div style={{ padding: '5px 0', margin: 0, fontSize: '16px' }}>
							Confirm Deletion
						</div>
					}
					open={isDeleteModalVisible}
					onCancel={handleCancelDelete}
					style={{ opacity: 1 }}
					centered
					footer={[
						<div
							style={{
								paddingTop: '10px',
							}}
							key="footer"
						>
							<Button
								key="cancel"
								type="primary"
								onClick={handleCancelDelete}
								style={{
									marginRight: '8px',
									backgroundColor: 'white',
									color: '#172014',
									borderColor: '#344735',
									fontSize: '12px',
									borderRadius: '12px',
								}}
							>
								Cancel
							</Button>
							<Button
								key="delete"
								type="primary"
								onClick={handleDelete}
								style={{
									backgroundColor: '#344735',
									borderColor: '#344735',
									fontSize: '12px',
									borderRadius: '12px',
								}}
							>
								Delete
							</Button>
						</div>,
					]}
				>
					<p
						style={{
							fontSize: '12px',
						}}
					>
						Are you sure you want to delete the allocation?
					</p>
					<Card style={{ padding: '10px', marginTop: '10px' }}>
						<p style={{ color: 'red', fontWeight: 600, fontSize: '12px' }}>
							Warning: Deleting this allocation will permanently remove all
							associated data from the selected pay period, including cost
							allocation information.
						</p>
					</Card>
				</Modal>
			)}
		</div>
	);
};

export default AllocationSettings;
